import {
	Children,
	forwardRef,
	HTMLAttributes,
	isValidElement,
	MutableRefObject,
	UIEvent,
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

/**
 * Windowed listbox for MUI Autocomplete.
 *
 * A catalog dropdown mounts one row component per option. Our item rows are not
 * cheap — each carries a price preview plus hover info/edit actions, and every
 * one of them subscribes to i18n and auth context — so a few thousand active
 * items means tens of thousands of mounted components and a listbox that takes
 * seconds to open. Rendering only the rows near the viewport keeps that cost
 * flat regardless of catalog size.
 *
 * Rows stay in normal document flow, padded by two spacer <li> elements rather
 * than absolute positioning. That matters: useAutocomplete scrolls the
 * highlighted option into view using `element.offsetTop`, so a virtual row must
 * sit at exactly the offset it would have occupied in a full list.
 */

/** Under this many options, plain rendering is cheap and measurement can't bite. */
const VIRTUALIZE_THRESHOLD = 120;

/**
 * Must exceed MUI's page step. useAutocomplete resolves the next highlighted
 * option with querySelector and treats a row that is not in the DOM as
 * disabled, so arrow (±1) and PageUp/PageDown (±5) must always land on a
 * rendered row or keyboard navigation stalls.
 */
const OVERSCAN = 8;

const FALLBACK_ROW_HEIGHT = 36;
const FALLBACK_VIEWPORT_HEIGHT = 320;

const SPACER_STYLE = {
	padding: 0,
	margin: 0,
	minHeight: 0,
	listStyle: "none",
} as const;

const isAriaSelected = (value: unknown) => value === true || value === "true";

/** Index of the current Autocomplete value in the (possibly filtered) option list. */
const findSelectedIndex = (nodes: ReturnType<typeof Children.toArray>): number => {
	for (let i = 0; i < nodes.length; i++) {
		const child = nodes[i];
		if (!isValidElement(child)) continue;
		const props = child.props as { "aria-selected"?: unknown; props?: { "aria-selected"?: unknown } };
		if (isAriaSelected(props["aria-selected"]) || isAriaSelected(props.props?.["aria-selected"])) {
			return i;
		}
	}
	return -1;
};

const scrollTopForIndex = (index: number, rowHeight: number, viewportHeight: number) => {
	if (index <= 0) return 0;
	return Math.max(0, index * rowHeight - Math.floor(viewportHeight / 4));
};

export const VirtualizedListbox = forwardRef<HTMLUListElement, HTMLAttributes<HTMLElement>>(
	function VirtualizedListbox(props, forwardedRef) {
		const { children, onScroll, ...listboxProps } = props;

		const nodeRef = useRef<HTMLUListElement | null>(null);
		const userScrolledRef = useRef(false);
		const [viewportHeight, setViewportHeight] = useState(FALLBACK_VIEWPORT_HEIGHT);
		const [rowHeight, setRowHeight] = useState(FALLBACK_ROW_HEIGHT);

		const options = useMemo(() => Children.toArray(children), [children]);
		const count = options.length;
		const selectedIndex = useMemo(() => findSelectedIndex(options), [options]);
		const virtualize = count > VIRTUALIZE_THRESHOLD;

		const [scrollTop, setScrollTop] = useState(() =>
			scrollTopForIndex(selectedIndex, FALLBACK_ROW_HEIGHT, FALLBACK_VIEWPORT_HEIGHT),
		);

		const setRefs = useCallback(
			(node: HTMLUListElement | null) => {
				nodeRef.current = node;
				if (typeof forwardedRef === "function") {
					forwardedRef(node);
				} else if (forwardedRef) {
					(forwardedRef as MutableRefObject<HTMLUListElement | null>).current = node;
				}
			},
			[forwardedRef],
		);

		// Row height is whatever this particular dropdown renders — POS rows are a
		// single line, cost+sell rows are two — so read it back instead of guessing.
		useLayoutEffect(() => {
			const node = nodeRef.current;
			if (!node || !virtualize) return;

			const measure = () => {
				setViewportHeight((prev) => node.clientHeight || prev);
				const row = node.querySelector<HTMLElement>("[data-option-index]");
				setRowHeight((prev) => row?.offsetHeight || prev);
			};

			measure();

			if (typeof ResizeObserver === "undefined") return;

			const observer = new ResizeObserver(measure);
			observer.observe(node);
			return () => observer.disconnect();
		}, [virtualize, count]);

		const jumpToSelected = (height = rowHeight, view = viewportHeight) => {
			const node = nodeRef.current;
			const top = scrollTopForIndex(selectedIndex, height, view);
			if (node) node.scrollTop = top;
			setScrollTop(top);
		};

		// Open / filter change: land on the selected item when it is in this list.
		useLayoutEffect(() => {
			userScrolledRef.current = false;
			jumpToSelected();
			// rowHeight is read from the latest render; filter/open is the trigger.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [count, selectedIndex]);

		// First real row-height measurement — keep the selected row in view.
		useLayoutEffect(() => {
			if (userScrolledRef.current) return;
			jumpToSelected();
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [rowHeight, viewportHeight]);

		useLayoutEffect(() => {
			if (virtualize) return;
			const selected = nodeRef.current?.querySelector<HTMLElement>('[aria-selected="true"]');
			selected?.scrollIntoView({ block: "nearest" });
		}, [virtualize, count, selectedIndex]);

		const handleScroll = useCallback(
			(event: UIEvent<HTMLElement>) => {
				userScrolledRef.current = true;
				setScrollTop(event.currentTarget.scrollTop);
				onScroll?.(event);
			},
			[onScroll],
		);

		if (!virtualize) {
			return (
				<ul ref={setRefs} {...listboxProps} onScroll={handleScroll}>
					{children}
				</ul>
			);
		}

		const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN);
		const endIndex = Math.min(
			count,
			startIndex + Math.ceil(viewportHeight / rowHeight) + OVERSCAN * 2,
		);

		const topSpacer = startIndex * rowHeight;
		const bottomSpacer = (count - endIndex) * rowHeight;

		return (
			<ul ref={setRefs} {...listboxProps} onScroll={handleScroll}>
				{topSpacer > 0 && (
					<li role="presentation" aria-hidden style={{ ...SPACER_STYLE, height: topSpacer }} />
				)}
				{options.slice(startIndex, endIndex)}
				{bottomSpacer > 0 && (
					<li role="presentation" aria-hidden style={{ ...SPACER_STYLE, height: bottomSpacer }} />
				)}
			</ul>
		);
	},
);

export default VirtualizedListbox;
